import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGameList, getAvailableGames } from 'app/api/game';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useGameManagement = () => {
  const [isdeleteFiltering, setIsdeleteFiltering] = useState(false);
  const navigate = useNavigate();
  const sectionObj = {
    sectionTitle: 'Game Management',
    searchObj: {
      keys: ['title', 'slug'],
      placeholder: 'Search by Title, Slug',
      searchType: 'server',

      handleSearch: (e) => {
        // server-side search handled by fetchGameData via PaginatedTable
      },
    },
    buttons: [
      {
        customclassName: '',
        imgTrue: true,
        title: APP_CONSTANTS.CREATE_GAME_TAG,
        handleBtnClick: (e) => {
          navigate(`${APP_CONSTANTS.CREATE_GAME}`);
        },
      },
    ],
  };

  const [pageCount, setPageCount] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const currentPage = 1;
  const dataPerPage = 10;
  const [filteredData, setFilteredData] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);

  // listGames currently ignores pagination server-side (01-02-SUMMARY.md
  // Known Stubs) -- fine for v1, render whatever array returns.
  const fetchGameData = useCallback(
    async (page, searchValue = '') => {
      setPageLoading(true);
      try {
        // Manifest fetch is best-effort: when the CDN is unreachable (or
        // GAMES_CDN_BASE unset) we render no CDN badges rather than failing
        // the list. A registered game missing from the manifest = its files
        // were removed from the deployed bundle ("Missing on CDN").
        const [response, availableRes] = await Promise.all([
          getGameList({
            page,
            limit: dataPerPage,
            search: searchValue,
          }),
          getAvailableGames().catch(() => null),
        ]);
        const manifestSlugs =
          availableRes?.responseCode === 200 &&
          Array.isArray(availableRes?.data?.games)
            ? new Set(availableRes.data.games.map((g) => g.slug))
            : null;
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.games || response?.data || [];
          const newArray = (Array.isArray(dataArr) ? dataArr : []).map(
            (item) => ({
              ...item,
              // LOWERCASE `status` -- PaginationTable.js reads
              // row.original.status for the 'Status' badge column.
              status: item.isEnabled,
              // true = files present on CDN; false = missing; null = unknown
              onCdn: manifestSlugs ? manifestSlugs.has(item.slug) : null,
            })
          );
          setFilteredData(newArray);

          const totalCount =
            response?.data?.count ?? (Array.isArray(dataArr) ? dataArr.length : 0);
          const tempcount = Math.ceil(totalCount / dataPerPage) || 1;
          setTotalNumber(totalCount);
          setPageCount(tempcount);
          setIsdeleteFiltering(false);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );

  // No delete endpoint exists on the backend for games -- keep this a
  // no-op so PaginatedTable's handleDelete prop stays safe to pass.
  const handleDelete = () => {};

  function handleEdit(data) {
    navigate(`/games/edit/:${data.id}`, {
      state: {
        ...data,
      },
    });
  }

  function handleViewLeaderboard(data) {
    navigate(`/games/leaderboard/:${data.id}`, {
      state: { id: data.id, title: data.title },
    });
  }

  return {
    handleDelete,
    pageLoading,
    currentPage,
    dataPerPage,
    pageCount,
    navigate,
    handleEdit,
    handleViewLeaderboard,
    sectionObj,
    totalNumber,
    filteredData,
    fetchGameData,
    isdeleteFiltering,
  };
};

export default useGameManagement;
